import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAI } from "../hooks/useAI";
import { useAuth } from "../AuthContext";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { countryName } from "../data/countries";
import {
  COLORS,
  SERIF,
  SANS,
  MONO,
  PROVIDER_COLORS,
  JUDGE_COLOR,
  pageStyles,
} from "../components/dispute/theme";

// The judge appends a fenced ```json … ``` block of structured output to its
// verdict. Strip it so only the human-readable judgement is shown.
function stripJsonFence(text) {
  if (!text) return "";
  return text.replace(/```(?:json)?\s*[\s\S]*?```\s*$/i, "").trim();
}

// Human-readable term for who authored a turn, used to label response sections.
function roleTerm(role) {
  if (role === "witness") return "witness";
  if (role === "disputant") return "disputant";
  if (role === "creator" || role === "claimant") return "claimant";
  return null;
}

export default function DisputeView() {
  const { uuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteEmail = searchParams.get("as");
  const [justFiled] = useState(searchParams.get("filed") === "1");
  const navigate = useNavigate();
  const { fetchSharedDispute, connectOnboard, connectStatus, claimPayout, submitFeedback, updateDisputeVisibility } = useAI();
  const { user, token } = useAuth();
  const [dispute, setDispute] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState(null);
  const [outcomeFeedback, setOutcomeFeedback] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const sendOutcomeFeedback = async () => {
    if (!outcomeFeedback.trim()) return;
    setFeedbackSaving(true);
    setFeedbackError(null);
    const res = await submitFeedback("outcome", outcomeFeedback.trim());
    if (res.ok) {
      setFeedbackSent(true);
    } else {
      setFeedbackError(res.error || "Failed to save feedback.");
    }
    setFeedbackSaving(false);
  };

  const loadThread = useCallback(async () => {
    const data = await fetchSharedDispute(uuid);
    if (data.error) {
      if (data.status === 401) {
        navigate("/login");
      } else if (data.status === 403) {
        alert("This dispute is private — only its parties can read it.");
        navigate("/home");
      } else {
        alert("Dispute not found");
        navigate("/home");
      }
      return;
    }
    setDispute(data);
  }, [uuid, fetchSharedDispute, navigate]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Keep private cases out of search results. Public ones are indexable — that's
  // the point of publishing them.
  useEffect(() => {
    if (dispute?.effective_public) return undefined;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [dispute?.effective_public]);

  if (!dispute) {
    return (
      <div style={pageStyles.root}>
        <SiteHeader />
        <div style={styles.loading}>Loading dispute…</div>
      </div>
    );
  }

  const responseHref = inviteEmail
    ? `/dispute-response/${uuid}?as=${encodeURIComponent(inviteEmail)}`
    : `/dispute-response/${uuid}`;
  const evidenceHref = `/dispute-evidence/${uuid}`;
  const jurisdictionLine = [countryName(dispute.jurisdiction_state), dispute.jurisdiction_region]
    .filter(Boolean)
    .join(" · ");

  // Once the panel has ruled, the case record is closed — no more responses.
  const isJudged = dispute.dispute_status === "judged";

  // Read-only public record: the server served this page to someone who isn't a
  // party, so identities, money and evidence files were stripped from it.
  const isPublicView = !!dispute.public;

  const isFiler =
    !!user && !!dispute.creator_email && user.email === dispute.creator_email;
  const isDisputant =
    !!user && !!dispute.disputant_email && user.email === dispute.disputant_email;

  // Whether the second disputant has added their side yet (any turn they
  // authored) — independent of dispute_status, which doesn't distinguish
  // "no one has responded" from "only a witness responded".
  const disputantResponded = (dispute.turns || []).some(
    (t) => t.query.author_role === "disputant"
  );

  const isWinner =
    !!user &&
    ((dispute.prevailing_party === "claimant" && user.email === dispute.creator_email) ||
      (dispute.prevailing_party === "disputant" && user.email === dispute.disputant_email));

  // Whether the current user has already added a response to this dispute.
  const myEmail = (user?.email || "").trim().toLowerCase();
  const hasMyResponse =
    !!myEmail &&
    (dispute.turns || []).some((t) => (t.query.author_email || "").trim().toLowerCase() === myEmail);
  // Each party (disputant or witness) gets a single response — once they've
  // added it, hide the add-response entry points. The filer never sees them,
  // and all are hidden once judged.
  const canRespond = !isFiler && !isJudged && !hasMyResponse;

  const publicUrl = `${window.location.origin}/dispute/${uuid}`;

  const togglePublic = async (next) => {
    setPublishing(true);
    setPublishError(null);
    const r = await updateDisputeVisibility(dispute.root_id, next);
    setPublishing(false);
    if (!r.ok) {
      setPublishError(r.error || "Could not save that setting.");
      return;
    }
    // The server resolves the filer's flag against any admin override and
    // returns the outcome — mirror it rather than assuming `next` won.
    setDispute((d) => ({
      ...d,
      is_public: r.is_public,
      admin_visibility: r.admin_visibility,
      visibility_locked: r.visibility_locked,
      flagged_public: r.flagged_public,
      effective_public: r.effective_public,
    }));
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure origin / permissions) — the URL is on
      // screen and selectable, so there's nothing to recover from.
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    setClaimMsg(null);
    const status = await connectStatus(uuid);
    if (!status.payoutsEnabled) {
      const onboard = await connectOnboard(uuid);
      if (onboard.url) {
        window.location.href = onboard.url; // off to Stripe Connect onboarding
        return;
      }
      setClaimMsg(onboard.error || "Could not start payout setup.");
      setClaiming(false);
      return;
    }
    const r = await claimPayout(uuid);
    setClaiming(false);
    if (r.error) setClaimMsg(r.error);
    else {
      setClaimMsg(`Payout sent: $${(r.amount / 100).toFixed(2)}`);
      loadThread();
    }
  };

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={styles.stack}>
          {/* Filer-only: publish or unpublish the final decision page. */}
          {isFiler && !isPublicView && (
            <div style={styles.visibilityCard}>
              <div style={styles.visibilityTop}>
                <div>
                  <div style={styles.visibilityTitle}>Public decision page</div>
                  <p style={styles.visibilityHint}>
                    {dispute.visibility_locked
                      ? `The platform has set this case to ${dispute.flagged_public ? "public" : "private"}. Your own setting is saved but has no effect while that stands.`
                      : "When on, anyone with the link can read the decision once the Neutrals have ruled. Names, email addresses and uploaded evidence files are never published."}
                  </p>
                </div>
                <label style={styles.visibilityToggle}>
                  <input
                    type="checkbox"
                    checked={!!dispute.is_public}
                    disabled={publishing || dispute.visibility_locked}
                    onChange={(e) => togglePublic(e.target.checked)}
                  />
                  <span>{dispute.is_public ? "Public" : "Private"}</span>
                </label>
              </div>

              {dispute.effective_public && (
                <div style={styles.visibilityLinkRow}>
                  <code style={styles.visibilityLink}>{publicUrl}</code>
                  <button type="button" style={styles.copyBtn} onClick={copyPublicLink}>
                    {linkCopied ? "Copied ✓" : "Copy link"}
                  </button>
                </div>
              )}
              {dispute.flagged_public && !dispute.effective_public && (
                <div style={styles.visibilityNote}>
                  Nothing is public yet — the page goes live once the panel has ruled.
                </div>
              )}
              {publishError && <div style={styles.visibilityError}>{publishError}</div>}
            </div>
          )}

          {["pending_payment", "awaiting_stake", "scheduled"].includes(dispute.dispute_status) && (
            <div style={styles.statusBanner}>
              {dispute.dispute_status === "pending_payment" && (
                <span>
                  <strong>Payment required.</strong> This case hasn't been filed yet —{" "}
                  <Link to={`/checkout/${uuid}`} style={styles.bannerLink}>complete payment</Link> to start.
                </span>
              )}
              {dispute.dispute_status === "awaiting_stake" && (
                isFiler ? (
                  <span>
                    <strong>Awaiting stake match.</strong> You staked an amount the other party can
                    match. Either way the panel delivers its verdict on{" "}
                    {dispute.judge_at ? new Date(dispute.judge_at).toLocaleString() : "the scheduled date"}.
                  </span>
                ) : isDisputant ? (
                  <span>
                    <strong>You've been invited to match the stake.</strong> The filer staked an
                    amount you can match —{" "}
                    <Link to={`/match-stake/${uuid}`} style={styles.bannerLink}>match the stake</Link>{" "}
                    to put it to the panel. Either way the panel delivers its verdict on{" "}
                    {dispute.judge_at ? new Date(dispute.judge_at).toLocaleString() : "the scheduled date"}.
                  </span>
                ) : (
                  <span>
                    <strong>Awaiting stake match.</strong> The filer staked an amount the other
                    party can match. Either way the panel delivers its verdict on{" "}
                    {dispute.judge_at ? new Date(dispute.judge_at).toLocaleString() : "the scheduled date"}.
                  </span>
                )
              )}
              {dispute.dispute_status === "scheduled" && (
                isFiler && !disputantResponded ? (
                  justFiled ? (
                    <span>
                      <strong>Dispute submitted.</strong> Your dispute has been submitted and an
                      email sent to the other party and any relevant witnesses.
                    </span>
                  ) : (
                    <span>
                      <strong>Dispute status:</strong> {dispute.disputant_email || "the other party"} has
                      not submitted their side of the dispute. Feel free to encourage them to do
                      so. They have until{" "}
                      {dispute.judge_at ? new Date(dispute.judge_at).toLocaleString() : "the scheduled date"}{" "}
                      to provide their information.
                    </span>
                  )
                ) : disputantResponded ? (
                  /* Once the disputant has put their side on record, the case is
                     only waiting on witnesses — say that instead of quoting the
                     judge_at date, which the panel may beat if no witness is due. */
                  <span>
                    <strong>Information Submitted.</strong> The Neutral AI panel will review the
                    information and deliver a decision once any witness information has been
                    provided. If there are no witnesses, the decision will be delivered
                    momentarily.
                  </span>
                ) : (
                  <span>
                    <strong>Verdict scheduled.</strong> The AI panel will deliver its verdict on{" "}
                    {dispute.judge_at ? new Date(dispute.judge_at).toLocaleString() : "the scheduled date"}.
                  </span>
                )
              )}
            </div>
          )}

          {dispute.dispute_status === "judged" && dispute.stake_amount && dispute.payout_status !== "none" && (
            <div style={styles.payoutBanner}>
              {dispute.payout_status === "refunded" ? (
                <span><strong>Tie.</strong> Both stakes were refunded.</span>
              ) : dispute.payout_status === "paid" ? (
                <span><strong>Payout complete.</strong> The winner has been paid their share of the pot.</span>
              ) : isWinner ? (
                <div style={styles.payoutClaimRow}>
                  <span>
                    <strong>You prevailed.</strong> Claim your share of the pot (
                    {100 - 25}% after the platform fee).
                  </span>
                  <button type="button" onClick={handleClaim} disabled={claiming} style={styles.claimBtn}>
                    {claiming ? "Working…" : "Claim payout"}
                  </button>
                  {claimMsg && <span style={styles.claimMsg}>{claimMsg}</span>}
                </div>
              ) : (
                <span>
                  <strong>{dispute.prevailing_party === "claimant" ? "The claimant" : "The disputant"} prevailed.</strong>{" "}
                  The pot is paid out to the winner.
                </span>
              )}
            </div>
          )}

          {dispute.turns.map((turn, tIdx) => {
            const isRoot = tIdx === 0;
            // "All AI on" = a full 3-judge panel that produced a judgement. For
            // those turns we show ONLY the judgement; solo turns show the answer.
            const answers = turn.results.filter((r) => !r.is_judgement);
            const judgement = turn.results.find((r) => r.is_judgement) || null;
            const isPanel = answers.length >= 3 && !!judgement;
            const authorTerm = roleTerm(turn.query.author_role);
            return (
              <div key={turn.query.id} style={styles.turnGroup}>
                {/* ── Navy card: dispute header (root only) + this turn's testimony ── */}
                <div style={styles.queryCard}>
                  {isRoot ? (
                    <div style={styles.headerBlock}>
                      <div style={styles.headerTop}>
                        <div style={{ minWidth: 0 }}>
                          <span style={styles.eyebrow}>Dispute</span>
                          <h1 style={styles.title}>{dispute.subject || "(no subject)"}</h1>
                          <div style={styles.metaLine}>
                            {(dispute.creator_name || dispute.creator_email) && (
                              <span>
                                Started by{" "}
                                <strong>{dispute.creator_name || dispute.creator_email}</strong>
                              </span>
                            )}
                            {dispute.category_name && (
                              <span style={styles.metaPill}>{dispute.category_name}</span>
                            )}
                            {jurisdictionLine && (
                              <span style={styles.metaPill}>{jurisdictionLine}</span>
                            )}
                          </div>
                        </div>
                        <div style={styles.headerActions}>
                          {canRespond && (
                            <Link to={responseHref} style={styles.continueBtn}>
                              ＋ Add your response
                            </Link>
                          )}
                          {/* Evidence files stay party-only, even on a published case. */}
                          {!isPublicView && (
                            <Link to={evidenceHref} style={styles.evidenceBtn}>
                              🗂 View Evidence
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span style={styles.turnEyebrow}>
                      {authorTerm ? `Response from ${authorTerm}` : "Response"} · Turn {tIdx + 1}
                    </span>
                  )}

                  <div style={styles.turnDate}>
                    {isRoot ? "Filed" : "Responded"}{" "}
                    {new Date(turn.query.created_at).toLocaleString()}
                  </div>

                  <div style={styles.fieldLabel}>Testimony</div>
                  <div style={styles.promptText}>{turn.query.prompt}</div>

                  {turn.query.desired_outcome && (
                    <div style={styles.outcomeBlock}>
                      <div style={styles.fieldLabel}>Desired outcome</div>
                      <div style={styles.promptText}>{turn.query.desired_outcome}</div>
                    </div>
                  )}
                </div>

                {isPanel ? (
                  /* Full panel: show only the judgement, not the individual answers. */
                  <div style={styles.judgeVerdictBox}>
                    <div style={styles.judgeVerdictHeader}>
                      ⚖ Panel verdict — judged by {judgement.provider} ({judgement.model})
                      {turn.winner ? ` · Winner: ${turn.winner.provider}` : ""}
                    </div>
                    <div style={styles.judgeVerdictText}>
                      {stripJsonFence(judgement.text) || "No verdict text available."}
                    </div>
                  </div>
                ) : (
                  /* Solo (or no judgement yet): show the individual answer(s). */
                  <div style={styles.answersGrid}>
                    {answers.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          ...styles.answerCard,
                          borderTop: `3px solid ${PROVIDER_COLORS[r.provider] || "#555"}`,
                        }}
                      >
                        <div style={styles.answerHeader}>
                          <span
                            style={{
                              ...styles.badge,
                              background: PROVIDER_COLORS[r.provider] || "#555",
                            }}
                          >
                            {r.provider}
                          </span>
                          <span style={styles.modelName}>{r.model}</span>
                        </div>
                        <div style={styles.answerText}>{stripJsonFence(r.text) || r.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Bottom CTA — hidden for the filer, once judged, and for a witness who
              has already responded; shown to others while the case is still open. */}
          {canRespond && (
            <Link to={responseHref} style={styles.bottomCta}>
              ＋ Continue thread / Add your response
            </Link>
          )}

          {/* Optional feedback on the verdict, once judged. Only for signed-in
              viewers — guests reading via the public share link have no
              account to attach the feedback to. */}
          {isJudged && token && !isPublicView && (
            <div style={styles.feedbackBox}>
              {feedbackSent ? (
                <p style={styles.feedbackSentText}>Thanks for the feedback on this outcome.</p>
              ) : (
                <>
                  <div style={styles.feedbackLabel}>Feedback on this outcome (optional)</div>
                  <p style={styles.hint}>
                    Did the verdict feel fair and well-reasoned? Anything the panel got wrong or missed?
                  </p>
                  <textarea
                    style={styles.feedbackTextarea}
                    placeholder="Tell us what you thought of this outcome."
                    value={outcomeFeedback}
                    onChange={(e) => setOutcomeFeedback(e.target.value)}
                    rows={3}
                  />
                  {feedbackError && <div style={styles.feedbackError}>{feedbackError}</div>}
                  <button
                    type="button"
                    style={styles.feedbackBtn}
                    disabled={feedbackSaving || !outcomeFeedback.trim()}
                    onClick={sendOutcomeFeedback}
                  >
                    {feedbackSaving ? "Sending…" : "Send feedback"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  loading: {
    display: "flex",
    height: "60vh",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },

  stack: {
    width: "100%",
    maxWidth: 880,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 40,
  },
  turnGroup: { display: "flex", flexDirection: "column", gap: 16 },
  statusBanner: {
    background: "#fdf6da",
    border: `1px solid ${JUDGE_COLOR}`,
    borderRadius: 12,
    padding: "16px 20px",
    fontSize: 14,
    lineHeight: 1.5,
    color: COLORS.ink,
  },
  bannerLink: { color: COLORS.ink, fontWeight: 700, textDecoration: "underline" },
  visibilityCard: {
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: "16px 20px",
    fontFamily: SANS,
    color: COLORS.ink,
  },
  visibilityTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  visibilityTitle: { fontSize: 14, fontWeight: 700, marginBottom: 4 },
  visibilityHint: {
    fontSize: 12,
    lineHeight: 1.5,
    color: COLORS.inkSoft,
    margin: 0,
    maxWidth: 560,
  },
  visibilityToggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  visibilityLinkRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    flexWrap: "wrap",
  },
  visibilityLink: {
    fontFamily: MONO,
    fontSize: 12,
    background: "#f4f4f0",
    borderRadius: 6,
    padding: "6px 10px",
    wordBreak: "break-all",
  },
  copyBtn: {
    background: "transparent",
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12,
    fontFamily: SANS,
    cursor: "pointer",
    color: COLORS.ink,
  },
  visibilityNote: { marginTop: 10, fontSize: 12, color: COLORS.inkSoft },
  visibilityError: { marginTop: 10, fontSize: 12, color: "#cc3333" },
  payoutBanner: {
    background: "#e8f5ee",
    border: "1px solid #1f6f43",
    borderRadius: 12,
    padding: "16px 20px",
    fontSize: 14,
    lineHeight: 1.5,
    color: COLORS.ink,
  },
  payoutClaimRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 },
  claimBtn: {
    background: "#1f6f43",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "9px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: SANS,
  },
  claimMsg: { fontSize: 13, color: "#1f6f43", fontWeight: 600 },

  // ── Query / header card ── background color test (was COLORS.dark navy).
  // Round 1: invite email's cream (#f3ede2, see buildDisputeEmail in
  // backend/src/routes/ai.js). Round 2: light turquoise tint rgba(64,224,208,
  // 0.16), then 0.08 (lighter). Round 3 (current): white + 1px turquoise
  // (#14A0C8) border. Dark ink text throughout. bottomCta/feedbackBtn below
  // now match this same turquoise too (were COLORS.dark, excluded from
  // earlier rounds, now included per later instruction).
  queryCard: {
    background: "#fff",
    color: COLORS.ink,
    padding: "28px 32px",
    borderRadius: "20px 20px 20px 4px",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #14A0C8",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.12)",
  },
  headerBlock: {
    borderBottom: `1px solid ${COLORS.border}`,
    paddingBottom: 20,
    marginBottom: 20,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
  },
  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  title: {
    fontFamily: SERIF,
    fontSize: 30,
    fontWeight: 600,
    margin: "6px 0 12px 0",
    letterSpacing: "-0.5px",
    color: COLORS.ink,
    lineHeight: 1.15,
  },
  metaLine: {
    fontSize: 13,
    color: COLORS.inkSoft,
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  metaPill: {
    background: COLORS.highlight,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: COLORS.inkSoft,
  },
  headerActions: { display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 },
  // Color test (see project_header_card_color_test memory): gray border → accent.
  continueBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "10px 18px",
    background: "#fff",
    color: COLORS.dark,
    border: "1px solid #14A0C8",
    borderRadius: 8,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 13,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // Color test (see project_header_card_color_test memory): gray border → accent.
  evidenceBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "10px 18px",
    background: "transparent",
    color: COLORS.ink,
    border: "1.5px solid #14A0C8",
    borderRadius: 8,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 13,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  turnEyebrow: {
    display: "block",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: COLORS.inkMuted,
    fontFamily: MONO,
  },
  turnDate: {
    fontFamily: MONO,
    fontSize: 11,
    color: COLORS.inkMuted,
    margin: "8px 0 16px 0",
  },
  fieldLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.5,
    color: COLORS.inkMuted,
    marginBottom: 6,
    fontFamily: SANS,
  },
  promptText: {
    fontSize: 15,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    fontFamily: SANS,
    color: COLORS.ink,
  },
  outcomeBlock: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: `1px solid ${COLORS.border}`,
  },

  // ── Answers ──
  answersGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%",
    boxSizing: "border-box",
  },
  answerCard: {
    background: COLORS.card,
    padding: 20,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    width: "100%",
    boxSizing: "border-box",
  },
  answerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  badge: {
    padding: "2px 8px",
    borderRadius: 4,
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    fontFamily: SANS,
  },
  modelName: { fontSize: 11, color: COLORS.inkMuted, fontFamily: MONO },
  winnerTag: {
    fontSize: 9,
    fontWeight: 900,
    color: "#000",
    background: JUDGE_COLOR,
    padding: "2px 6px",
    borderRadius: 4,
    border: `1px solid ${JUDGE_COLOR}`,
  },
  answerText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: COLORS.ink,
    whiteSpace: "pre-wrap",
  },

  judgeVerdictBox: {
    background: "#fdf6da",
    padding: 20,
    borderRadius: 12,
    borderLeft: `6px solid ${JUDGE_COLOR}`,
    border: `1px solid ${COLORS.border}`,
    width: "100%",
    boxSizing: "border-box",
  },
  judgeVerdictHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: "#8a5a00",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 1.5,
  },
  judgeVerdictText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: COLORS.ink,
    whiteSpace: "pre-wrap",
  },

  bottomCta: {
    display: "block",
    width: "100%",
    padding: "16px 20px",
    background: "#1A5EC4",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 15,
    textAlign: "center",
    textDecoration: "none",
    cursor: "pointer",
  },

  feedbackBox: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: "20px 22px",
  },
  feedbackLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.5,
    color: COLORS.inkMuted,
    marginBottom: 6,
    fontFamily: SANS,
  },
  hint: { color: COLORS.inkMuted, fontSize: 12.5, lineHeight: 1.5, margin: "0 0 12px 0", fontFamily: SANS },
  feedbackTextarea: {
    width: "100%",
    boxSizing: "border-box",
    background: COLORS.bg,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
    fontFamily: SANS,
    color: COLORS.ink,
    outline: "none",
    resize: "vertical",
    marginBottom: 12,
  },
  feedbackBtn: {
    background: "#1A5EC4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 13.5,
    fontWeight: 600,
    fontFamily: SANS,
    cursor: "pointer",
  },
  feedbackError: { color: "#c0392b", fontSize: 12.5, marginBottom: 10, fontFamily: SANS },
  feedbackSentText: { color: COLORS.inkSoft, fontSize: 13.5, fontFamily: SANS, margin: 0 },
};
